import React, { createContext, useContext, useState, ReactNode } from 'react';
import '../components/Common/Modal.css';

export interface ModalOptions {
  title: string;
  message: string;
  type?: 'info' | 'warning' | 'error' | 'success' | 'confirm';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

interface ModalContextType {
  showModal: (options: ModalOptions) => void;
  hideModal: () => void;
  isVisible: boolean;
  modalOptions: ModalOptions | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);

  const showModal = (options: ModalOptions) => {
    setModalOptions(options);
    setIsVisible(true);
  };

  const hideModal = () => {
    setIsVisible(false);
    setModalOptions(null);
  };

  const handleConfirm = () => {
    if (modalOptions?.onConfirm) {
      modalOptions.onConfirm();
    }
    hideModal();
  };

  const handleCancel = () => {
    if (modalOptions?.onCancel) {
      modalOptions.onCancel();
    }
    hideModal();
  };

  const value: ModalContextType = {
    showModal,
    hideModal,
    isVisible,
    modalOptions
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {isVisible && modalOptions && (
        <Modal
          options={modalOptions}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ModalContext.Provider>
  );
};

// Composant Modal
interface ModalProps {
  options: ModalOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

const Modal: React.FC<ModalProps> = ({ options, onConfirm, onCancel }) => {
  const getIcon = () => {
    switch (options.type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'confirm':
        return '❓';
      default:
        return 'ℹ️';
    }
  };

  const getTypeClass = () => {
    switch (options.type) {
      case 'success':
        return 'modal-success';
      case 'error':
        return 'modal-error';
      case 'warning':
        return 'modal-warning';
      case 'confirm':
        return 'modal-confirm';
      default:
        return 'modal-info';
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className={`modal ${getTypeClass()}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-icon">{getIcon()}</div>
          <h3 className="modal-title">{options.title}</h3>
        </div>
        
        <div className="modal-body">
          <p className="modal-message">{options.message}</p>
        </div>
        
        <div className="modal-footer">
          {options.showCancel !== false && (
            <button 
              className="btn btn-secondary modal-cancel"
              onClick={onCancel}
            >
              {options.cancelText || 'Annuler'}
            </button>
          )}
          <button 
            className={`btn ${options.type === 'error' ? 'btn-danger' : 'btn-primary'} modal-confirm`}
            onClick={onConfirm}
          >
            {options.confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};
